import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import { useToast } from "../../hooks/useToast";
import { API_BASE } from "../../config/apiConfig";
import { FaCheck, FaTimes } from "react-icons/fa";
import {
  resetActivities,
} from "../../features/activities/activitiesSlice";

export default function ActivityForm({ isEdit }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");
  const { showToast, ToastContainer } = useToast();
  const dispatch = useDispatch();

  const location = useLocation();
  const from = location.state?.from;
  const coachId = location.state?.coachId;

  const defaultPath = isEdit ? `/activities/${id}` : "/manage-activities";
  const backTo = from === "other-coach-side"
    ? `/other-coaches-activities-list/${coachId}`
    : defaultPath;

  const [loading, setLoading] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");
  const [type, setType] = useState("");
  const [rankingType, setRankingType] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [visibility, setVisibility] = useState("public");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [remarks, setRemarks] = useState("");

  // ------------------------------------------------------------
  // Load existing data (EDIT mode)
  // ------------------------------------------------------------
  useEffect(() => {
    if (isEdit && id) loadActivity();
  }, [isEdit, id]);

  async function loadActivity() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/activities-detail/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        const a = data.activity;
        setName(a.name || "");
        setStatus(a.status || "active");
        setType(a.type || "");
        setRankingType(a.rankingType || "");
        setStartDate(a.startDate || today);
        setEndDate(a.endDate || today);
        setVisibility(a.visibility || "public");
        setStartTime(a.startTime || "");
        setEndTime(a.endTime || "");
        setRemarks(a.remarks || "");
      }
    } finally {
      setLoading(false);
    }
  }

  // ------------------------------------------------------------
  // VALIDATION
  // ------------------------------------------------------------
  function validateForm() {
    if (!name.trim()) return "Activity name is required.";
    if (!status) return "Status is required.";
    if (!type) return "Type is required.";
    if (type === "tournament" && !rankingType)
      return "Ranking Type is required for tournaments.";
    if (!startDate) return "Start Date is required.";
    if (!endDate) return "End Date is required.";
    if (startDate > endDate) return "End Date cannot be earlier than Start Date.";

    return null;
  }

  const canSubmit =
    name.trim() &&
    status &&
    type &&
    startDate &&
    endDate &&
    (type !== "tournament" || rankingType);


  // ------------------------------------------------------------
  // SUBMIT FORM (Create or Edit)
  // ------------------------------------------------------------
  async function handleSubmit() {
    const err = validateForm();
    if (err) {
      showToast(err, "error");
      return;
    }

    const payload = {
      name,
      status,
      type,
      startDate,
      endDate,
      rankingType: type === "tournament" ? rankingType : null,
      visibility,
      startTime,
      endTime,
      remarks,
    };

    setLoading(true);

    try {
      let res;

      if (isEdit) {
        // -------- UPDATE --------
        res = await fetch(`${API_BASE}/api/activities-update/${id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } else {
        // -------- CREATE --------
        res = await fetch(`${API_BASE}/api/activities-create`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();

      if (data.success) {
        // *****************************************************
        // RESET REDUX CACHE FOR NEW LISTING
        // *****************************************************
        dispatch(resetActivities());

        navigate(backTo);
      } else {
        showToast(data.message || "Error occurred", "error");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />

      <PageHeader
        title={isEdit ? "Edit Activity" : "Add New Activity"}
        backTo={isEdit ? `/activities/${id}` : "/manage-activities"}
      />

      <div className="max-w-2xl mx-auto w-full space-y-6 text-left">
        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`p-1 rounded text-white transition ${
              canSubmit
                ? "bg-green-600 hover:bg-green-800"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            title="Submit"
          >
            <FaCheck size={20} />
          </button>
          <button
            onClick={() => navigate(backTo)}
            className="p-1 bg-gray-300 rounded"
            title="Cancel"
          >
            <FaTimes size={20} />
          </button>
        </div>
        
        {/* Activity Name */}
        <div>
          <label className="block mb-1 font-medium">
            Activity Name <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="border rounded p-3 w-full placeholder:italic placeholder:text-gray-400"
            placeholder="Enter activity name..."
          />
        </div>

        {/* Status */}
        <div>
          <label className="block mb-1 font-medium">
            Status <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="border rounded p-3 w-full"
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="draft">Draft</option>
            <option value="full">Full</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Type */}
        <div>
          <label className="block mb-1 font-medium">
            Type <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            value={type}
            onChange={e => {
              setType(e.target.value);
              if (e.target.value !== "tournament") setRankingType("");
            }}
            className="border rounded p-3 w-full"
          >
            <option value="">Select type</option>
            <option value="event">Event</option>
            <option value="tournament">Tournament</option>
            <option value="camp">Camp</option>
            <option value="seminar">Seminar</option>
            <option value="workshop">Workshop</option>
            <option value="talk">Talk</option>
            <option value="sparring">Sparring</option>
            <option value="fitness">Fitness</option>
            <option value="friendlyMatch">Friendly Match</option>
          </select>
        </div>

        {/* Ranking Type (only if tournament) */}
        {type === "tournament" && (
          <div>
            <label className="block mb-1 font-medium">
              Ranking Type <span className="text-red-500 italic text-xs">*Required</span>
            </label>
            <select
              value={rankingType}
              onChange={e => setRankingType(e.target.value)}
              className="border rounded p-3 w-full"
            >
              <option value="">Select ranking type</option>
              <option value="National">National</option>
              <option value="AJSS">AJSS</option>
            </select>
          </div>
        )}

        {/* Start Date */}
        <div>
          <label className="block mb-1 font-medium">
            Start Date <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border rounded p-3 w-full"
          />
        </div>

        {/* Start Time */}
        <div>
          <label className="block mb-1 font-medium">
            Start Time <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="time"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            className="border rounded p-3 w-full"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block mb-1 font-medium">
            End Date <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="border rounded p-3 w-full"
          />
        </div>

        {/* End Time */}
        <div>
          <label className="block mb-1 font-medium">
            End Time <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="time"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            className="border rounded p-3 w-full"
          />
        </div>

        {/* Remarks */}
        <div>
          <label className="block mb-1 font-medium">
            Remarks <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            className="border rounded p-3 w-full placeholder:italic placeholder:text-gray-400"
            rows={3}
            placeholder="Add any notes about the activity..."
          />
        </div>
      </div>
      <ToastContainer />
    </MainContentLayout>
  );
}
